/**
 * SSO con www.walkycam.com cuando la app se carga dentro de su iframe.
 *
 * WalkyCam abre `https://www.fastkaraoke.com/?sso=walkycam` y entrega su JWT por
 * postMessage (nunca por URL):
 *
 *   iframe → padre : { type: 'walkycam:sso:ready' }
 *   padre → iframe : { type: 'walkycam:sso:token', token }
 *   iframe → padre : { type: 'walkycam:sso:error' }   (token rechazado por la API)
 */

const DEFAULT_ALLOWED_ORIGINS = ['https://www.walkycam.com', 'https://walkycam.com'];
const SSO_TIMEOUT_MS = 4000;

const getAllowedOrigins = (): string[] => {
  const fromEnv = import.meta.env.VITE_SSO_ALLOWED_ORIGINS;
  if (!fromEnv) return DEFAULT_ALLOWED_ORIGINS;
  return fromEnv
    .split(',')
    .map((origin) => origin.trim().replace(/\/+$/, ''))
    .filter(Boolean);
};

const isAllowedOrigin = (origin: string): boolean => {
  if (getAllowedOrigins().includes(origin)) return true;
  // En desarrollo se acepta WalkyCam corriendo en localhost con cualquier puerto
  return import.meta.env.DEV && /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
};

export const isWalkyCamSsoEmbed = (): boolean => {
  if (typeof window === 'undefined' || window.parent === window) return false;
  return new URLSearchParams(window.location.search).get('sso') === 'walkycam';
};

let parentOrigin: string | null = null;

/** Pide el token al padre. Resuelve null si no llega uno válido a tiempo. */
export function requestWalkyCamSsoToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const finish = (token: string | null) => {
      window.removeEventListener('message', onMessage);
      clearTimeout(timer);
      resolve(token);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || !isAllowedOrigin(event.origin)) return;
      if (event.data?.type !== 'walkycam:sso:token') return;

      const token = typeof event.data.token === 'string' ? event.data.token : '';
      parentOrigin = event.origin;
      finish(token || null);
    };

    const timer = setTimeout(() => {
      console.warn('[WalkyAuth] SSO: el padre no envió token a tiempo');
      finish(null);
    }, SSO_TIMEOUT_MS);

    window.addEventListener('message', onMessage);
    // El mensaje "ready" no contiene datos sensibles; el token solo se acepta de orígenes permitidos
    window.parent.postMessage({ type: 'walkycam:sso:ready' }, '*');
  });
}

export function notifyWalkyCamSsoError(): void {
  if (parentOrigin) {
    window.parent.postMessage({ type: 'walkycam:sso:error' }, parentOrigin);
  }
}
